import React, { useState } from 'react';
import { safeClipboardWrite } from '../utils/safeStorage';
import { createPortal } from 'react-dom';
import { XIcon, MailIcon, ClipboardCopyIcon, CheckIcon } from './icons';

interface AddParticipantsModalProps {
  onClose: () => void;
  onInvite: (emails: string[]) => void;
  projectLink: string;
}

const AddParticipantsModal: React.FC<AddParticipantsModalProps> = ({ onClose, onInvite, projectLink }) => {
  const [activeTab, setActiveTab] = useState<'email' | 'link'>('email');
  const [emails, setEmails] = useState('');
  const [copied, setCopied] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'success'>('idle');
  const [sentCount, setSentCount] = useState(0);

  const handleInvite = () => {
    setEmailError(null);
    const emailList = emails.split(/[\n,;\s]+/).map(e => e.trim()).filter(Boolean);

    if (emailList.length === 0) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const email of emailList) {
      if (!emailRegex.test(email)) {
        setEmailError(`Invalid email format: "${email}". Please correct it and try again.`);
        return;
      }
    }

    onInvite(emailList);
    setSentCount(emailList.length);
    setSubmissionStatus('success');
  };

  const handleInviteMore = () => {
    setEmails('');
    setEmailError(null);
    setSentCount(0);
    setSubmissionStatus('idle');
  };

  const handleCopyLink = () => {
    safeClipboardWrite(projectLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 p-4 flex items-center justify-center" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-app-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-text-ink">Add Participants</h2>
            <button type="button" onClick={onClose} className="p-1 rounded-full text-text-muted hover:bg-neutral-100 active:bg-neutral-200">
              <XIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex p-1 bg-neutral-100 rounded-lg">
            <button
              onClick={() => setActiveTab('email')}
              className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all active:scale-95 ${activeTab === 'email' ? 'bg-white shadow text-text-ink' : 'text-text-muted hover:bg-neutral-200'}`}
            >
              Invite by Email
            </button>
            <button
              onClick={() => setActiveTab('link')}
              className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all active:scale-95 ${activeTab === 'link' ? 'bg-white shadow text-text-ink' : 'text-text-muted hover:bg-neutral-200'}`}
            >
              Share Link
            </button>
          </div>

          {activeTab === 'email' && (
            <div className="animate-fade-in-down">
              {submissionStatus === 'idle' ? (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="emails" className="block text-xs font-medium text-text-muted mb-1">
                      Participant emails
                    </label>
                    <textarea
                      id="emails"
                      value={emails}
                      onChange={(e) => {
                        setEmails(e.target.value);
                        if (emailError) setEmailError(null);
                      }}
                      placeholder="Enter emails, separated by commas, spaces, or new lines."
                      rows={5}
                      className={`w-full p-3 bg-white border rounded-lg focus:outline-none text-sm ${emailError
                        ? 'border-status-warn ring-1 ring-status-warn'
                        : 'border-app-border focus:border-action focus:ring-1 focus:ring-action'
                        }`}
                    />
                    {emailError && <p className="mt-2 text-sm text-status-warn">{emailError}</p>}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-status-ok-bg text-status-ok rounded-full inline-flex items-center justify-center">
                    <CheckIcon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-bold text-text-ink mt-4">Invitations Sent!</h3>
                  <p className="text-text-muted mt-2">
                    Successfully sent {sentCount} invitation{sentCount !== 1 && 's'}.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'link' && (
            <div className="space-y-4 animate-fade-in-down">
              <div>
                <p className="text-sm text-text-muted mb-2" style={{ textWrap: 'pretty' }}>Share this private link to allow participants to join your project directly.</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={projectLink}
                    className="w-full bg-neutral-100 border-transparent rounded-lg shadow-sm sm:text-sm text-text-muted focus:outline-none focus:ring-2 focus:ring-action px-3 py-2"
                  />
                  <button
                    onClick={handleCopyLink}
                    className="flex-shrink-0 flex items-center gap-2 bg-white border border-app-border text-text-ink font-semibold py-2 px-3 rounded-lg hover:bg-neutral-100 active:bg-neutral-200 transition-colors text-sm"
                  >
                    {copied ? <CheckIcon className="w-5 h-5 text-status-ok" /> : <ClipboardCopyIcon className="w-5 h-5" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-app-bg rounded-b-2xl flex justify-end gap-3">
          {submissionStatus === 'success' ? (
            <>
              <button type="button" onClick={onClose} className="bg-white border border-app-border text-text-ink text-sm font-medium py-2 px-4 rounded-lg hover:bg-app-bg transition-colors">
                Close
              </button>
              <button type="button" onClick={handleInviteMore} className="flex items-center gap-2 bg-action text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-action/90 active:scale-95 transition-colors">
                Invite More
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={onClose} className="bg-white border border-app-border text-text-ink text-sm font-medium py-2 px-4 rounded-lg hover:bg-app-bg transition-colors">
                Cancel
              </button>
              {activeTab === 'email' && (
                <button
                  type="button"
                  onClick={handleInvite}
                  disabled={emails.trim() === ''}
                  className="flex items-center gap-2 bg-action text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-action/90 active:scale-95 transition-colors disabled:bg-neutral-300 disabled:text-text-muted disabled:cursor-not-allowed"
                >
                  <MailIcon className="w-5 h-5" /> Send Invites
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddParticipantsModal;
